import { all, call, put, takeLatest } from '@redux-saga/core/effects';
import axios, { AxiosResponse } from 'axios';

import { SagaPayload } from '../../api.types';
import {
  ArchiveProgrammeRequest,
  ArchiveProgrammeResponse,
  CreateProgrammeRequest,
  CreateProgrammeResponse,
  PublishProgrammeRequest,
  PublishProgrammeResponse,
  UpdateParentProgrammeRequest,
  UpdateParentProgrammeResponse,
  UpdateSubProgrammeRequest,
  UpdateSubProgrammeResponse,
  ViewAllProgrammesResponse,
  ViewParentProgrammeRequest,
  ViewParentProgrammeResponse,
  ViewSubProgrammeRequest,
  ViewSubProgrammeResponse
} from './programme.types';

import {
  ARCHIVING_PROGRAMME,
  CREATING_PROGRAMME,
  PUBLISHING_PROGRAMME,
  RETRIEVE_ALL_PROGRAMMES,
  RETRIEVE_PARENT_PROGRAMME,
  RETRIEVE_SUB_PROGRAMME,
  UPDATING_PARENT_PROGRAMME,
  UPDATING_SUB_PROGRAMME
} from './programme.constants';

import {
  requestArchiveProgramme,
  requestCreateProgramme,
  requestPublishProgramme,
  requestUpdateParentProgramme,
  requestUpdateSubProgramme,
  requestViewAllProgrammes,
  requestViewParentProgramme,
  requestViewSubProgramme
} from './programme.actions';

import {
  getArchiveProgrammeRequest,
  getCreateProgrammeRequest,
  getPublishProgrammeRequest,
  getUpdateParentProgrammeRequest,
  getUpdateSubProgrammeRequest,
  getViewAllProgrammesRequest,
  getViewParentProgrammeRequest,
  getViewSubProgrammeRequest
} from './programme.endpoints';

import { setParentProgramme, setProgrammeSummaries, setSubProgramme } from '../../../slices/programme/programme.slice';
import { addSpinner, addSystemNotice, removeSpinner } from '../../../slices/system/system.slice';

import { getErrorMessage } from '../../../utils/api.util';
import { ERROR, SUCCESS } from '../../../utils/constants';

export function* performViewAllProgrammesRequest() {
  try {
    yield put(addSpinner(RETRIEVE_ALL_PROGRAMMES));

    const [endpoint, options] = getViewAllProgrammesRequest();

    const { data }: AxiosResponse<ViewAllProgrammesResponse> = yield call(axios, endpoint, options);

    yield put(setProgrammeSummaries(data.data.programmeSummaries));
  } catch (error) {
    yield put(addSystemNotice({ type: ERROR, message: getErrorMessage(error) }));
  } finally {
    yield put(removeSpinner(RETRIEVE_ALL_PROGRAMMES));
  }
}

export function* watchForViewAllProgrammesRequest() {
  yield takeLatest(requestViewAllProgrammes.type, performViewAllProgrammesRequest);
}

export function* performCreateProgrammeRequest({
  payload
}: SagaPayload<CreateProgrammeRequest, CreateProgrammeResponse>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(CREATING_PROGRAMME));

    const [endpoint, options] = getCreateProgrammeRequest(requestData);

    const { data }: AxiosResponse<CreateProgrammeResponse> = yield call(axios, endpoint, options);

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));

    if (onSuccess) {
      yield call(onSuccess, data);
    }
  } catch (error) {
    yield put(addSystemNotice({ type: ERROR, message: getErrorMessage(error) }));
  } finally {
    yield put(removeSpinner(CREATING_PROGRAMME));
  }
}

export function* watchForCreateProgrammeRequest() {
  yield takeLatest(requestCreateProgramme.type, performCreateProgrammeRequest);
}

export function* performViewParentProgrammeRequest({
  payload
}: SagaPayload<ViewParentProgrammeRequest, ViewParentProgrammeResponse['data']>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(RETRIEVE_PARENT_PROGRAMME));

    const [endpoint, options] = getViewParentProgrammeRequest(requestData);

    const { data }: AxiosResponse<ViewParentProgrammeResponse> = yield call(axios, endpoint, options);

    data.data.subProgrammeSummaries.sort((subProgrammeSummaryOne, subProgrammeSummaryTwo) =>
      subProgrammeSummaryOne.name.localeCompare(subProgrammeSummaryTwo.name)
    );

    yield put(setParentProgramme(data.data));

    if (onSuccess) {
      yield call(onSuccess, data.data);
    }
  } catch (error) {
    yield put(addSystemNotice({ type: ERROR, message: getErrorMessage(error) }));
  } finally {
    yield put(removeSpinner(RETRIEVE_PARENT_PROGRAMME));
  }
}

export function* watchForViewParentProgrammeRequest() {
  yield takeLatest(requestViewParentProgramme.type, performViewParentProgrammeRequest);
}

export function* performViewSubProgrammeRequest({
  payload
}: SagaPayload<ViewSubProgrammeRequest, ViewSubProgrammeResponse['data']>) {
  const { requestData } = payload;

  try {
    yield put(addSpinner(RETRIEVE_SUB_PROGRAMME));

    const [endpoint, options] = getViewSubProgrammeRequest(requestData);

    const { data }: AxiosResponse<ViewSubProgrammeResponse> = yield call(axios, endpoint, options);

    yield put(setSubProgramme(data.data));
  } catch (error) {
    yield put(addSystemNotice({ type: ERROR, message: getErrorMessage(error) }));
  } finally {
    yield put(removeSpinner(RETRIEVE_SUB_PROGRAMME));
  }
}

export function* watchForViewSubProgrammeRequest() {
  yield takeLatest(requestViewSubProgramme.type, performViewSubProgrammeRequest);
}

export function* performUpdateParentProgrammeRequest({
  payload
}: SagaPayload<UpdateParentProgrammeRequest, UpdateParentProgrammeResponse['data']>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(UPDATING_PARENT_PROGRAMME));

    const [endpoint, options] = getUpdateParentProgrammeRequest(requestData);

    const { data }: AxiosResponse<UpdateParentProgrammeResponse> = yield call(axios, endpoint, options);

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));

    if (onSuccess) {
      yield call(onSuccess, data.data);
    }
  } catch (error) {
    yield put(addSystemNotice({ type: ERROR, message: getErrorMessage(error) }));
  } finally {
    yield put(removeSpinner(UPDATING_PARENT_PROGRAMME));
  }
}

export function* watchForUpdateParentProgrammeRequest() {
  yield takeLatest(requestUpdateParentProgramme.type, performUpdateParentProgrammeRequest);
}

export function* performUpdateSubProgrammeRequest({
  payload
}: SagaPayload<UpdateSubProgrammeRequest, UpdateSubProgrammeResponse['data']>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(UPDATING_SUB_PROGRAMME));

    const [endpoint, options] = getUpdateSubProgrammeRequest(requestData);

    const { data }: AxiosResponse<UpdateSubProgrammeResponse> = yield call(axios, endpoint, options);

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));

    if (onSuccess) {
      yield call(onSuccess, data.data);
    }
  } catch (error) {
    yield put(addSystemNotice({ type: ERROR, message: getErrorMessage(error) }));
  } finally {
    yield put(removeSpinner(UPDATING_SUB_PROGRAMME));
  }
}

export function* watchForUpdateSubProgrammeRequest() {
  yield takeLatest(requestUpdateSubProgramme.type, performUpdateSubProgrammeRequest);
}

export function* performPublishProgrammeRequest({ payload }: SagaPayload<PublishProgrammeRequest, string>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(PUBLISHING_PROGRAMME));

    const [endpoint, options] = getPublishProgrammeRequest(requestData);

    const { data }: AxiosResponse<PublishProgrammeResponse> = yield call(axios, endpoint, options);

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));

    if (onSuccess) {
      yield call(onSuccess, data.data.programmeExternalReference);
    }
  } catch (error) {
    yield put(addSystemNotice({ type: ERROR, message: getErrorMessage(error) }));
  } finally {
    yield put(removeSpinner(PUBLISHING_PROGRAMME));
  }
}

export function* watchForPublishProgrammeRequest() {
  yield takeLatest(requestPublishProgramme.type, performPublishProgrammeRequest);
}

export function* performArchiveProgrammeRequest({
  payload
}: SagaPayload<ArchiveProgrammeRequest, ArchiveProgrammeResponse>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(ARCHIVING_PROGRAMME));

    const [endpoint, options] = getArchiveProgrammeRequest(requestData);

    const { data }: AxiosResponse<ArchiveProgrammeResponse> = yield call(axios, endpoint, options);

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));

    if (onSuccess) {
      yield call(onSuccess, data);
    }
  } catch (error) {
    yield put(addSystemNotice({ type: ERROR, message: getErrorMessage(error) }));
  } finally {
    yield put(removeSpinner(ARCHIVING_PROGRAMME));
  }
}

export function* watchForArchiveProgrammeRequest() {
  yield takeLatest(requestArchiveProgramme.type, performArchiveProgrammeRequest);
}

export default function* programmeSaga() {
  yield all([
    watchForViewAllProgrammesRequest(),
    watchForCreateProgrammeRequest(),
    watchForViewParentProgrammeRequest(),
    watchForViewSubProgrammeRequest(),
    watchForUpdateParentProgrammeRequest(),
    watchForUpdateSubProgrammeRequest(),
    watchForPublishProgrammeRequest(),
    watchForArchiveProgrammeRequest()
  ]);
}
